import { useState, useEffect } from "react";
import { API_ENDPOINTS } from "../../../constants/constants";

interface Quiz {
  _id: string;
  id?: string;
  title?: string;
  name?: string;
  description?: string; // Ajout du champ description manquant
  createdAt: string | null;
  moduleId?: number;
  coursId?: number;
  cours?: {
    id: number;
    title?: string;
    name?: string;
  };
}

interface QuizFormData {
  title: string;
  description: string;
  isActive: boolean;
  timeLimit: number;
  passingScore: number;
  shuffleQuestions: boolean;
  shuffleAnswers: boolean;
  maxAttempts: number;
  showCorrectAnswers: boolean;
  moduleId: number;
  coursId: number;
  isUnlocked: boolean;
  questions: Question[];
}

interface Question {
  _id?: string;
  id?: string;
  text: string;
  explanation: string;
  points: number;
  type: string;
  hasImage: boolean;
  imageUrl: string;
  answers: Answer[];
}

interface Answer {
  _id?: string;
  id?: string;
  questionId?: string;
  text: string;
  isCorrect: boolean;
  hasImage: boolean;
  imageUrl: string;
  feedback: string;
}

// Définir une interface QuizDetails plus complète
interface QuizDetails extends Quiz {
  description?: string;
  isActive?: boolean;
  timeLimit?: number;
  passingScore?: number;
  shuffleQuestions?: boolean;
  shuffleAnswers?: boolean;
  maxAttempts?: number;
  showCorrectAnswers?: boolean;
  isUnlocked?: boolean;
  moduleId?: number;
  questions?: QuizQuestion[];
}

interface QuizQuestion {
  _id?: string;
  id?: string;
  text: string;
  explanation?: string;
  points: number;
  type: string;
  hasImage?: boolean;
  imageUrl?: string;
  answers: QuizAnswer[];
}

interface QuizAnswer {
  _id?: string;
  id?: string;
  questionId?: string;
  text: string;
  isCorrect: boolean;
  hasImage?: boolean;
  imageUrl?: string;
  feedback?: string;
}

const defaultQuizFormData: QuizFormData = {
  title: "",
  description: "",
  isActive: true,
  timeLimit: 10,
  passingScore: 70,
  shuffleQuestions: false,
  shuffleAnswers: false,
  maxAttempts: 3,
  showCorrectAnswers: false,
  moduleId: 1,
  coursId: 0,
  isUnlocked: false,
  questions: [],
};

const defaultQuestion: Question = {
  text: "",
  explanation: "",
  points: 1,
  type: "qcm",
  hasImage: false,
  imageUrl: "",
  answers: [],
};

const defaultAnswer: Answer = {
  text: "",
  isCorrect: false,
  hasImage: false,
  imageUrl: "",
  feedback: "",
};

const AdminEvaluation = () => {
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [createLoading, setCreateLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [formData, setFormData] = useState<QuizFormData>(defaultQuizFormData);
  const [courses, setCourses] = useState<{ id: number; title: string }[]>([]);
  const [loadingCourses, setLoadingCourses] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [currentQuestion, setCurrentQuestion] = useState<Question>({
    ...defaultQuestion,
    answers: [{ ...defaultAnswer }],
  });
  const [selectedQuiz, setSelectedQuiz] = useState<Quiz | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [quizDetails, setQuizDetails] = useState<QuizDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [modulesCourses, setModulesCourses] = useState<
    { id: number; title: string; moduleId: number }[]
  >([]);
  const [loadingModuleCourses, setLoadingModuleCourses] =
    useState<boolean>(false);

  useEffect(() => {
    fetchQuizzes();
    fetchCourses();
  }, []);

  const fetchQuizzes = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }
      setLoading(true);
      const response = await fetch(API_ENDPOINTS.QUIZZ.GET_ALL_QUIZZES, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error("Erreur lors de la récupération des quizzes");
      }

      const data = await response.json();
      console.log("Données quizzes reçues:", data);

      const normalizedData = Array.isArray(data)
        ? data.map((quiz) => {
            // S'assurer que toutes les propriétés existent et sont du bon type
            return {
              ...quiz,
              _id:
                quiz._id ||
                quiz.id ||
                Math.random().toString(36).substring(2, 15),
              title: quiz.title || quiz.name || "Quiz sans titre",
              createdAt: quiz.createdAt || null,
              moduleId: quiz.moduleId || undefined,
              coursId: quiz.coursId || (quiz.cours ? quiz.cours.id : undefined),
              cours: quiz.cours || undefined,
            };
          })
        : [];

      setQuizzes(normalizedData);
      setError(null);
    } catch (err) {
      console.error("Erreur:", err);
      setError(
        "Impossible de charger les quizzes. Veuillez réessayer plus tard."
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      setLoadingCourses(true);
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/cours/modules`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Erreur lors de la récupération des modules");
      }

      const data = await response.json();
      // Définir un type pour les données de modules
      interface ModuleData {
        id: number;
        title?: string;
        name?: string;
        description?: string;
        moduleId?: number;
        createdAt?: string;
        updatedAt?: string;
      }

      const modulesData = data.map((module: ModuleData) => ({
        id: module.id,
        title: module.title || module.name || "Module sans titre",
      }));

      setCourses(modulesData);

      // Si des modules sont disponibles, définir le premier comme valeur par défaut et charger ses cours
      if (modulesData.length > 0) {
        const firstModuleId = modulesData[0].id;
        setFormData((prev) => ({
          ...prev,
          moduleId: firstModuleId,
        }));
        fetchModuleCourses(firstModuleId);
      }
    } catch (err) {
      console.error("Erreur:", err);
    } finally {
      setLoadingCourses(false);
    }
  };

  // Fonction pour récupérer les cours d'un module spécifique
  const fetchModuleCourses = async (moduleId: number) => {
    try {
      setLoadingModuleCourses(true);
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/cours/module/${moduleId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Erreur lors de la récupération des cours du module");
      }

      const data = await response.json();

      const moduleCourses = Array.isArray(data)
        ? data.map((course) => ({
            id: course.id,
            title: course.title || course.name || "Cours sans titre",
            moduleId: moduleId,
          }))
        : [];

      setModulesCourses(moduleCourses);

      // Si des cours sont disponibles pour ce module, définir le premier comme valeur par défaut
      if (moduleCourses.length > 0) {
        setFormData((prev) => ({
          ...prev,
          coursId: moduleCourses[0].id,
        }));
      } else {
        // Si aucun cours n'est disponible, réinitialiser l'ID du cours
        setFormData((prev) => ({
          ...prev,
          coursId: 0,
        }));
      }
    } catch (err) {
      console.error("Erreur lors de la récupération des cours du module:", err);
    } finally {
      setLoadingModuleCourses(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target as HTMLInputElement;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? (e.target as HTMLInputElement).checked
          : type === "number" || name === "coursId" || name === "moduleId"
          ? Number.parseInt(value, 10) || 0
          : value,
    }));

    // Si le module a changé, récupérer les cours associés
    if (name === "moduleId") {
      const moduleId = Number.parseInt(value, 10);
      fetchModuleCourses(moduleId);
    }
  };

  const handleAddQuestion = () => {
    // Vérifier que la question a au moins une réponse si c'est un QCM ou QCU
    if (
      (currentQuestion.type === "qcm" || currentQuestion.type === "qcu") &&
      currentQuestion.answers.length === 0
    ) {
      alert("Veuillez ajouter au moins une réponse pour cette question.");
      return;
    }

    // Vérifier qu'au moins une réponse est marquée comme correcte pour les QCM et QCU
    if (currentQuestion.type === "qcm" || currentQuestion.type === "qcu") {
      const hasCorrectAnswer = currentQuestion.answers.some(
        (answer) => answer.isCorrect
      );
      if (!hasCorrectAnswer) {
        alert("Veuillez marquer au moins une réponse comme correcte.");
        return;
      }
    }

    // Vérifier qu'une seule réponse est marquée comme correcte pour les QCU
    if (currentQuestion.type === "qcu") {
      const correctAnswers = currentQuestion.answers.filter(
        (answer) => answer.isCorrect
      );
      if (correctAnswers.length > 1) {
        alert(
          "Pour un QCU (choix unique), une seule réponse doit être marquée comme correcte."
        );
        return;
      }
    }

    if (
      isEditMode &&
      currentStep > 0 &&
      currentStep <= formData.questions.length
    ) {
      // En mode édition, nous modifions une question existante
      const updatedQuestions = [...formData.questions];
      updatedQuestions[currentStep - 1] = currentQuestion;

      setFormData((prev) => ({
        ...prev,
        questions: updatedQuestions,
      }));

      // Passer à une nouvelle question ou rester sur l'actuelle
      if (currentStep === formData.questions.length) {
        // Si c'était la dernière question, proposer d'en ajouter une nouvelle
        setCurrentQuestion({
          ...defaultQuestion,
          answers: [{ ...defaultAnswer }],
        });
        setCurrentStep(currentStep + 1);
      } else {
        // Sinon, charger la question suivante
        loadExistingQuestion(currentStep);
      }
    } else {
      // Mode création: ajouter la question actuelle au formulaire
      setFormData((prev) => ({
        ...prev,
        questions: [...prev.questions, currentQuestion],
      }));

      // Passer à une nouvelle question
      setCurrentQuestion({
        ...defaultQuestion,
        answers: [{ ...defaultAnswer }],
      });
      setCurrentStep(currentStep + 1);
    }
  };

  const handleQuestionInputChange = (
    field: keyof Question,
    value: string | number | boolean
  ) => {
    setCurrentQuestion((prev) => ({
      ...prev,
      [field]:
        field === "hasImage"
          ? value
          : field === "points"
          ? Number.parseInt(value.toString(), 10)
          : value,
    }));
  };

  const handleAddAnswer = () => {
    // Récupérer l'ID de la question en cours d'édition
    const questionId = currentQuestion._id || currentQuestion.id;

    setCurrentQuestion((prev) => ({
      ...prev,
      answers: [
        ...prev.answers,
        {
          ...defaultAnswer,
          // Ajouter questionId uniquement si on est en mode édition et que la question a un ID
          ...(isEditMode && questionId ? { questionId } : {}),
        },
      ],
    }));
  };

  const handleRemoveAnswer = (answerIndex: number) => {
    setCurrentQuestion((prev) => ({
      ...prev,
      answers: prev.answers.filter((_, i) => i !== answerIndex),
    }));
  };

  const handleAnswerInputChange = (
    answerIndex: number,
    field: keyof Answer,
    value: string | boolean
  ) => {
    setCurrentQuestion((prev) => {
      const updatedAnswers = [...prev.answers];
      updatedAnswers[answerIndex] = {
        ...updatedAnswers[answerIndex],
        [field]: value,
      };
      return {
        ...prev,
        answers: updatedAnswers,
      };
    });
  };

  const handlePrevious = () => {
    if (currentStep === 0) {
      // Si on est sur les informations générales, fermer le modal
      setShowModal(false);
    } else if (currentStep === 1) {
      // Si on est sur la première question, revenir aux informations générales
      setCurrentStep(0);
    } else {
      if (isEditMode) {
        // Mode édition
        // Sauvegarder les modifications sur la question actuelle si nécessaire
        if (currentStep <= formData.questions.length) {
          const updatedQuestions = [...formData.questions];
          updatedQuestions[currentStep - 1] = currentQuestion;
          setFormData((prev) => ({
            ...prev,
            questions: updatedQuestions,
          }));
        }

        // Revenir à la question précédente
        loadExistingQuestion(currentStep - 2);
      } else {
        // Mode création
        // Enregistrer l'état actuel et revenir à la question précédente
        const questionIndex = currentStep - 2;
        if (questionIndex >= 0 && questionIndex < formData.questions.length) {
          // Charger la question précédente
          const previousQuestion = formData.questions[questionIndex];
          setCurrentQuestion(previousQuestion);

          // Mettre à jour le formData pour supprimer la dernière question
          setFormData((prev) => ({
            ...prev,
            questions: prev.questions.slice(0, -1),
          }));

          setCurrentStep(currentStep - 1);
        }
      }
    }
  };

  const resetQuizCreation = () => {
    setFormData(defaultQuizFormData);
    setCurrentQuestion({ ...defaultQuestion, answers: [{ ...defaultAnswer }] });
    setCurrentStep(0);
    setShowModal(false);
    setIsEditMode(false);
    setSelectedQuiz(null);
  };

  const createQuiz = async (e: React.FormEvent) => {
    e.preventDefault();

    // Si nous sommes sur une question pas encore ajoutée, l'ajouter d'abord
    let finalFormData = formData;
    if (currentStep > 0 && currentQuestion.text) {
      finalFormData = {
        ...formData,
        questions: [...formData.questions, currentQuestion],
      };
    }

    try {
      setCreateLoading(true);
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      // Créer une copie des données sans moduleId car l'API attend uniquement coursId
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { moduleId, ...dataWithoutModuleId } = finalFormData;

      // S'assurer que coursId est bien un nombre
      const dataToSend = {
        ...dataWithoutModuleId,
        coursId:
          typeof dataWithoutModuleId.coursId === "string"
            ? Number.parseInt(dataWithoutModuleId.coursId, 10) || 0
            : dataWithoutModuleId.coursId,
      };

      const response = await fetch(API_ENDPOINTS.QUIZZ.CREATE_QUIZZ, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(dataToSend),
      });

      if (!response.ok) {
        throw new Error("Erreur lors de la création du quiz");
      }

      await fetchQuizzes();
      resetQuizCreation();
    } catch (err) {
      console.error("Erreur:", err);
      setError("Impossible de créer le quiz. Veuillez réessayer plus tard.");
    } finally {
      setCreateLoading(false);
    }
  };

  // Filtrer les quizzes en fonction du terme de recherche
  const filteredQuizzes = quizzes.filter((quiz) =>
    (quiz.title || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Formater la date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Non spécifiée";

    try {
      const date = new Date(dateString);
      // Vérifier si la date est valide
      if (Number.isNaN(date.getTime())) {
        return "Non spécifiée";
      }

      // Format jour/mois/année heure:minute
      const jour = date.getDate().toString().padStart(2, "0");
      const mois = (date.getMonth() + 1).toString().padStart(2, "0");
      const annee = date.getFullYear();
      const heures = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");

      return `${jour}/${mois}/${annee} ${heures}:${minutes}`;
    } catch {
      return "Non spécifiée";
    }
  };

  // Fonction pour obtenir le nom du cours associé au quiz
  const getCourseName = (quiz: Quiz) => {
    // Vérifier si l'objet quiz existe
    if (!quiz) return "Non associé";

    // Si le quiz a une propriété cours directement
    if (quiz.cours?.title || quiz.cours?.name) {
      return quiz.cours.title || quiz.cours.name;
    }

    // Sinon, chercher dans la liste des cours du module sélectionné
    if (quiz.coursId && modulesCourses.length > 0) {
      const course = modulesCourses.find((c) => c.id === quiz.coursId);
      if (course) return course.title;
    }

    // Enfin, chercher dans la liste générale des cours
    if (quiz.coursId && courses.length > 0) {
      const course = courses.find((c) => c.id === quiz.coursId);
      return course ? course.title : "Non associé";
    }

    return "Non associé";
  };

  // Ajout d'une fonction pour obtenir le nom du module associé
  const getModuleName = (moduleId?: number) => {
    if (!moduleId) return "Non associé";

    const module = courses.find((m) => m.id === moduleId);
    return module ? module.title : "Non associé";
  };

  // Fonction pour charger une question existante en mode édition
  const loadExistingQuestion = (questionIndex: number) => {
    if (questionIndex >= 0 && questionIndex < formData.questions.length) {
      // S'assurer que chaque réponse a tous les champs nécessaires
      const question = { ...formData.questions[questionIndex] };

      // S'assurer que les réponses sont correctement formatées
      const formattedAnswers = question.answers.map((answer) => ({
        ...answer, // Préserver tous les champs originaux, y compris questionId
        text: answer.text || "",
        isCorrect: answer.isCorrect ?? false,
        hasImage: answer.hasImage || false,
        imageUrl: answer.imageUrl || "",
        feedback: answer.feedback || "",
      }));

      // Mettre à jour la question avec les réponses formatées
      const formattedQuestion = {
        ...question,
        answers: formattedAnswers,
      };

      setCurrentQuestion(formattedQuestion);
      setCurrentStep(questionIndex + 1);
    }
  };

  // Rendu de l'étape selon currentStep
  const renderStep = () => {
    if (currentStep === 0) {
      // Étape 0: Informations générales du quiz
      return (
        <div className="bg-white p-4 rounded-md shadow-sm">
          <h4 className="text-md font-medium mb-4">Informations générales</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="quiz-title" className="block text-sm font-medium text-gray-700 mb-1">
                Titre*
              </label>
              <input
                id="quiz-title"
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                required
              />
            </div>
            <div>
              <label htmlFor="quiz-description" className="block text-sm font-medium text-gray-700 mb-1">
                Description*
              </label>
              <input
                id="quiz-description"
                type="text"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                required
              />
            </div>
            <div>
              <label htmlFor="quiz-timeLimit" className="block text-sm font-medium text-gray-700 mb-1">
                Limite de temps (minutes)
              </label>
              <input
                id="quiz-timeLimit"
                type="number"
                name="timeLimit"
                value={formData.timeLimit}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                min="1"
              />
            </div>
            <div>
              <label htmlFor="quiz-passingScore" className="block text-sm font-medium text-gray-700 mb-1">
                Score de passage (%)
              </label>
              <input
                id="quiz-passingScore"
                type="number"
                name="passingScore"
                value={formData.passingScore}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                min="0"
                max="100"
              />
            </div>
            <div>
              <label htmlFor="quiz-maxAttempts" className="block text-sm font-medium text-gray-700 mb-1">
                Nombre de tentatives maximum
              </label>
              <input
                id="quiz-maxAttempts"
                type="number"
                name="maxAttempts"
                value={formData.maxAttempts}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                min="1"
              />
            </div>
            <div>
              <label htmlFor="quiz-moduleId" className="block text-sm font-medium text-gray-700 mb-1">
                Module associé*
              </label>
              {loadingCourses ? (
                <div className="flex items-center p-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600 mr-2"></div>
                  <span>Chargement des modules...</span>
                </div>
              ) : (
                <select
                  id="quiz-moduleId"
                  name="moduleId"
                  value={formData.moduleId}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-md"
                  required
                >
                  {courses.length > 0 ? (
                    courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.title}
                      </option>
                    ))
                  ) : (
                    <option value="">Aucun module disponible</option>
                  )}
                </select>
              )}
            </div>
            <div>
              <label htmlFor="quiz-coursId" className="block text-sm font-medium text-gray-700 mb-1">
                Cours associé*
              </label>
              {loadingModuleCourses ? (
                <div className="flex items-center p-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600 mr-2"></div>
                  <span>Chargement des cours...</span>
                </div>
              ) : (
                <select
                  id="quiz-coursId"
                  name="coursId"
                  value={formData.coursId}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-md"
                  required
                  disabled={modulesCourses.length === 0}
                >
                  {modulesCourses.length > 0 ? (
                    modulesCourses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.title}
                      </option>
                    ))
                  ) : (
                    <option value="0">
                      Aucun cours disponible pour ce module
                    </option>
                  )}
                </select>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
              />
              <span className="ml-2 block text-sm text-gray-700">Actif</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                name="shuffleQuestions"
                checked={formData.shuffleQuestions}
                onChange={handleInputChange}
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
              />
              <span className="ml-2 block text-sm text-gray-700">
                Mélanger les questions
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                name="shuffleAnswers"
                checked={formData.shuffleAnswers}
                onChange={handleInputChange}
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
              />
              <span className="ml-2 block text-sm text-gray-700">
                Mélanger les réponses
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                name="showCorrectAnswers"
                checked={formData.showCorrectAnswers}
                onChange={handleInputChange}
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
              />
              <span className="ml-2 block text-sm text-gray-700">
                Montrer les réponses correctes
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                name="isUnlocked"
                checked={formData.isUnlocked}
                onChange={handleInputChange}
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
              />
              <span className="ml-2 block text-sm text-gray-700">
                Déverrouillé
              </span>
            </label>
          </div>

          {/* Afficher les questions existantes en mode édition */}
          {isEditMode && formData.questions.length > 0 && (
            <div className="mt-6 border-t pt-4">
              <h4 className="text-md font-medium mb-4">
                Questions existantes ({formData.questions.length})
              </h4>
              <div className="overflow-y-auto max-h-60 border rounded-md p-2">
                {formData.questions.map((question, qIndex) => (
                  <button
                    key={qIndex}
                    className="w-full text-left p-2 mb-2 border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                    onClick={() => loadExistingQuestion(qIndex)}
                    type="button"
                  >
                    <div className="flex justify-between items-center">
                      <h6 className="font-medium">Question {qIndex + 1}</h6>
                      <span className="text-sm text-gray-500">
                        {question.type === "qcm"
                          ? "QCM"
                          : question.type === "qcu"
                          ? "QCU"
                          : "Texte"}{" "}
                        - {question.points} pts
                      </span>
                    </div>
                    <p className="truncate text-sm">{question.text}</p>
                    <div className="mt-1 flex justify-between">
                      <span className="text-xs text-gray-500">
                        {question.answers.length} réponse(s)
                      </span>
                      <span className="text-xs text-blue-500">
                        Cliquer pour modifier
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    } else {
      // Étape > 0: Création/édition d'une question
      // Si nous sommes en mode édition et que l'index de question existe déjà
      const isEditingExistingQuestion =
        isEditMode && currentStep <= formData.questions.length;

      return (
        <div className="bg-white p-4 rounded-md shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-md font-medium">
              {isEditingExistingQuestion
                ? `Modification de la question ${currentStep}`
                : `Nouvelle question ${currentStep}`}
            </h4>

            {/* Navigation entre questions en mode édition */}
            {isEditMode && formData.questions.length > 1 && (
              <div className="flex space-x-2">
                <select
                  className="border rounded p-1 text-sm"
                  value={currentStep - 1}
                  onChange={(e) =>
                    loadExistingQuestion(Number.parseInt(e.target.value))
                  }
                >
                  {formData.questions.map((_, qIndex) => (
                    <option key={qIndex} value={qIndex}>
                      Question {qIndex + 1}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {isEditMode && (
            <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-700">
                <strong>Mode édition :</strong> Vous pouvez modifier les détails
                de la question et ses réponses. N'oubliez pas de cliquer sur
                "Enregistrer et passer à la suivante" pour sauvegarder vos
                modifications.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="question-text" className="block text-sm font-medium text-gray-700 mb-1">
                Texte de la question*
              </label>
              <input
                id="question-text"
                type="text"
                value={currentQuestion.text}
                onChange={(e) =>
                  handleQuestionInputChange("text", e.target.value)
                }
                className="w-full p-2 border rounded-md"
                required
              />
            </div>
            <div>
              <label htmlFor="question-explanation" className="block text-sm font-medium text-gray-700 mb-1">
                Explication
              </label>
              <input
                id="question-explanation"
                type="text"
                value={currentQuestion.explanation}
                onChange={(e) =>
                  handleQuestionInputChange("explanation", e.target.value)
                }
                className="w-full p-2 border rounded-md"
              />
            </div>
            <div>
              <label htmlFor="question-points" className="block text-sm font-medium text-gray-700 mb-1">
                Points
              </label>
              <input
                id="question-points"
                type="number"
                value={currentQuestion.points}
                onChange={(e) =>
                  handleQuestionInputChange("points", e.target.value)
                }
                className="w-full p-2 border rounded-md"
                min="1"
              />
            </div>
            <div>
              <label htmlFor="question-type" className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                id="question-type"
                value={currentQuestion.type}
                onChange={(e) =>
                  handleQuestionInputChange("type", e.target.value)
                }
                className="w-full p-2 border rounded-md"
              >
                <option value="qcm">QCM</option>
                <option value="qcu">QCU</option>
                <option value="texte">Texte</option>
              </select>
            </div>
          </div>

          <div className="flex items-center mb-4">
            <input
              id="question-hasImage"
              type="checkbox"
              checked={currentQuestion.hasImage}
              onChange={(e) =>
                handleQuestionInputChange("hasImage", e.target.checked)
              }
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
            />
            <label htmlFor="question-hasImage" className="ml-2 block text-sm text-gray-700">
              Ajouter une image
            </label>
          </div>

          {currentQuestion.hasImage && (
            <div className="mb-4">
              <label htmlFor="question-imageUrl" className="block text-sm font-medium text-gray-700 mb-1">
                URL de l'image
              </label>
              <input
                id="question-imageUrl"
                type="text"
                value={currentQuestion.imageUrl}
                onChange={(e) =>
                  handleQuestionInputChange("imageUrl", e.target.value)
                }
                className="w-full p-2 border rounded-md"
              />
            </div>
          )}

          {/* Réponses */}
          <div className="mt-4">
            <div className="flex justify-between items-center mb-2">
              <h6 className="text-sm font-medium">
                Réponses
                {isEditMode && (
                  <span className="ml-2 text-indigo-600">
                    ({currentQuestion.answers.length} réponse(s))
                  </span>
                )}
              </h6>

              {isEditMode &&
                (currentQuestion.type === "qcm" ||
                  currentQuestion.type === "qcu") && (
                  <div className="text-xs text-gray-500">
                    N'oubliez pas de marquer au moins une réponse comme correcte
                  </div>
                )}
            </div>

            {currentQuestion.answers.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-2">
                Aucune réponse ajoutée.
              </p>
            ) : (
              <div className="space-y-3">
                {currentQuestion.answers.map((answer, aIndex) => (
                  <div
                    key={aIndex}
                    className="border border-gray-200 rounded-md p-3"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <h6
                        className={`text-xs font-medium ${
                          answer.isCorrect ? "text-green-600" : ""
                        }`}
                      >
                        Réponse {aIndex + 1}
                        {answer.isCorrect && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            Correcte
                          </span>
                        )}
                      </h6>
                      <button
                        type="button"
                        onClick={() => handleRemoveAnswer(aIndex)}
                        className="text-red-600 hover:text-red-800"
                        title="Supprimer cette réponse"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <label htmlFor={`answer-text-${aIndex}`} className="block text-xs font-medium text-gray-700 mb-1">
                          Texte de la réponse*
                        </label>
                        <input
                          id={`answer-text-${aIndex}`}
                          type="text"
                          value={answer.text}
                          onChange={(e) =>
                            handleAnswerInputChange(
                              aIndex,
                              "text",
                              e.target.value
                            )
                          }
                          className="w-full p-2 border rounded-md"
                          required
                        />
                      </div>

                      <div className="flex items-center">
                        <input
                          id={`answer-isCorrect-${aIndex}`}
                          type="checkbox"
                          checked={answer.isCorrect}
                          onChange={(e) =>
                            handleAnswerInputChange(
                              aIndex,
                              "isCorrect",
                              e.target.checked
                            )
                          }
                          className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                        />
                        <label htmlFor={`answer-isCorrect-${aIndex}`} className="ml-2 block text-xs text-gray-700">
                          Réponse correcte
                        </label>
                      </div>

                      <div className="flex items-center">
                        <input
                          id={`answer-hasImage-${aIndex}`}
                          type="checkbox"
                          checked={answer.hasImage}
                          onChange={(e) =>
                            handleAnswerInputChange(
                              aIndex,
                              "hasImage",
                              e.target.checked
                            )
                          }
                          className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                        />
                        <label htmlFor={`answer-hasImage-${aIndex}`} className="ml-2 block text-xs text-gray-700">
                          Ajouter une image
                        </label>
                      </div>

                      {answer.hasImage && (
                        <div>
                          <label htmlFor={`answer-imageUrl-${aIndex}`} className="block text-xs font-medium text-gray-700 mb-1">
                            URL de l'image
                          </label>
                          <input
                            id={`answer-imageUrl-${aIndex}`}
                            type="text"
                            value={answer.imageUrl}
                            onChange={(e) =>
                              handleAnswerInputChange(
                                aIndex,
                                "imageUrl",
                                e.target.value
                              )
                            }
                            className="w-full p-2 border rounded-md"
                          />
                        </div>
                      )}

                      <div>
                        <label htmlFor={`answer-feedback-${aIndex}`} className="block text-xs font-medium text-gray-700 mb-1">
                          Feedback
                        </label>
                        <input
                          id={`answer-feedback-${aIndex}`}
                          type="text"
                          value={answer.feedback}
                          onChange={(e) =>
                            handleAnswerInputChange(
                              aIndex,
                              "feedback",
                              e.target.value
                            )
                          }
                          className="w-full p-2 border rounded-md"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Bouton d'ajout de réponse */}
            <button
              type="button"
              onClick={handleAddAnswer}
              className="bg-indigo-600 text-white px-3 py-2 rounded-md hover:bg-indigo-700 text-sm flex items-center mt-3 mx-auto"
            >
              <svg
                className="w-4 h-4 mr-1"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
              Ajouter une réponse
            </button>

            {currentQuestion.type === "qcm" && (
              <p className="text-xs text-gray-500 text-center mt-2">
                Pour un QCM (choix multiple), vous pouvez marquer plusieurs
                réponses comme correctes.
              </p>
            )}
            {currentQuestion.type === "qcu" && (
              <p className="text-xs text-gray-500 text-center mt-2">
                Pour un QCU (choix unique), veuillez ne marquer qu'une seule
                réponse comme correcte.
              </p>
            )}
          </div>
        </div>
      );
    }
  };

  // Ajouter une nouvelle fonction pour voir les détails d'un quiz
  const viewQuizDetails = async (quizId: string) => {
    try {
      setLoadingDetails(true);
      setShowDetailsModal(true);

      const selectedQuiz = quizzes.find(
        (q) => q._id === quizId || q.id === quizId
      );
      setSelectedQuiz(selectedQuiz || null);

      // Sans appeler l'API qui renvoie une erreur 403, utiliser directement les données locales
      console.log("Affichage des données locales du quiz:", selectedQuiz);

      if (selectedQuiz) {
        const localQuizDetails = {
          ...selectedQuiz,
          title: selectedQuiz.title || "Quiz sans titre",
          description:
            "Les détails complets ne sont pas disponibles actuellement. Problème d'API.",
          createdAt: selectedQuiz.createdAt || null,
          moduleId: selectedQuiz.moduleId || undefined,
          coursId: selectedQuiz.coursId || selectedQuiz.cours?.id,
          cours: selectedQuiz.cours,
          questions: [], // Pas de questions disponibles pour le moment
        };

        setQuizDetails(localQuizDetails);
      } else {
        throw new Error("Quiz non trouvé");
      }
    } catch (err) {
      console.error("Erreur lors de l'affichage des détails:", err);
      if (selectedQuiz) {
        setQuizDetails({
          ...selectedQuiz,
          questions: [],
        });
      }
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedQuiz(null);
    setQuizDetails(null);
  };

  // Fonction pour éditer un quiz existant
  const handleEditQuiz = async (quizId: string) => {
    try {
      // Récupérer d'abord les détails du quiz
      const selectedQuiz = quizzes.find(
        (q) => q._id === quizId || q.id === quizId
      );

      if (!selectedQuiz) {
        throw new Error("Quiz non trouvé");
      }

      setLoading(true);

      console.log(
        "Utilisation des données locales pour éditer le quiz:",
        selectedQuiz
      );

      // Créer un formulaire avec les données locales uniquement
      const moduleId = selectedQuiz.moduleId || 1;

      // Charger les cours du module
      await fetchModuleCourses(moduleId);

      // Préparer les données pour l'édition avec les données minimales disponibles
      const editFormData = {
        title: selectedQuiz.title || "",
        description: "Description non disponible",
        isActive: true,
        timeLimit: 10,
        passingScore: 70,
        shuffleQuestions: false,
        shuffleAnswers: false,
        maxAttempts: 3,
        showCorrectAnswers: false,
        moduleId: moduleId,
        coursId:
          typeof selectedQuiz.coursId === "string"
            ? Number.parseInt(selectedQuiz.coursId, 10) || 0
            : selectedQuiz.coursId ||
              (selectedQuiz.cours ? selectedQuiz.cours.id : 0),
        isUnlocked: false,
        questions: [], // Pas de questions disponibles pour le moment
      };

      // Mettre à jour l'état et ouvrir le modal
      setFormData(editFormData);
      setCurrentStep(0);
      setShowModal(true);

      // Stocker l'ID du quiz en cours d'édition
      setSelectedQuiz(selectedQuiz);
      setIsEditMode(true);
    } catch (err) {
      console.error("Erreur lors de l'édition:", err);
      setError("Impossible de charger le quiz pour édition.");
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour sauvegarder les modifications d'un quiz
  const updateQuiz = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedQuiz) {
      setError("Aucun quiz sélectionné pour la mise à jour");
      return;
    }

    // Préparer les données pour la mise à jour
    const updatedQuestions = [...formData.questions];

    // Si nous sommes sur une question en cours d'édition, l'intégrer
    if (currentStep > 0) {
      if (currentStep <= formData.questions.length) {
        // Mettre à jour une question existante
        updatedQuestions[currentStep - 1] = {
          ...updatedQuestions[currentStep - 1], // Préserver les propriétés originales comme _id
          ...currentQuestion, // Écraser avec les nouvelles valeurs
          // S'assurer que les réponses conservent leurs propriétés importantes
          answers: currentQuestion.answers.map((answer) => ({
            ...answer,
            // S'assurer que questionId est défini pour chaque réponse
            questionId:
              answer.questionId ||
              updatedQuestions[currentStep - 1]._id ||
              updatedQuestions[currentStep - 1].id,
          })),
        };
      } else if (currentQuestion.text) {
        // Ajouter une nouvelle question
        updatedQuestions.push(currentQuestion);
      }
    }

    // Créer l'objet final des données avec tous les champs nécessaires
    const formDataWithQuestions = {
      ...formData,
      questions: updatedQuestions,
      _id: selectedQuiz._id, // S'assurer que l'ID du quiz est inclus
      id: selectedQuiz.id, // Inclure aussi id au cas où
    };

    // S'assurer que coursId est bien un nombre
    const finalFormData = {
      ...formDataWithQuestions,
      coursId:
        typeof formDataWithQuestions.coursId === "string"
          ? Number.parseInt(formDataWithQuestions.coursId, 10) || 0
          : formDataWithQuestions.coursId,
    };

    try {
      setCreateLoading(true);

      // Problème avec l'API, on simule une mise à jour réussie
      console.log(
        "Simulation de mise à jour du quiz (l'API actuelle retourne une erreur 403)"
      );
      console.log("Données qui auraient été envoyées:", finalFormData);

      // Au lieu d'appeler l'API, on rafraîchit simplement la liste
      await fetchQuizzes();

      // Afficher un message à l'utilisateur
      alert(
        "L'API de mise à jour des quizzes n'est pas disponible actuellement. Les modifications n'ont pas été sauvegardées sur le serveur."
      );

      // Réinitialiser l'interface
      resetQuizCreation();
      setSelectedQuiz(null);
      setIsEditMode(false);
    } catch (err) {
      console.error("Erreur lors de la simulation de mise à jour:", err);
      setError(
        "Impossible de mettre à jour le quiz. Veuillez réessayer plus tard."
      );
    } finally {
      setCreateLoading(false);
    }
  };

  // Fonction pour supprimer un quiz
  const handleDeleteQuiz = async (quizId: string) => {
    if (
      !window.confirm(
        "Êtes-vous sûr de vouloir supprimer ce quiz? Cette action est irréversible."
      )
    ) {
      return;
    }

    try {
      setLoading(true);

      // Problème avec l'API, on simule une suppression réussie
      console.log(
        "Simulation de suppression du quiz (l'API actuelle retourne une erreur 403)"
      );
      console.log("Quiz qui aurait été supprimé:", quizId);

      // Au lieu d'appeler l'API, on simule une suppression en rafraîchissant la liste
      // Ce n'est pas idéal, mais ça évite l'erreur 403
      await fetchQuizzes();

      // Afficher un message à l'utilisateur
      alert(
        "L'API de suppression des quizzes n'est pas disponible actuellement. Le quiz n'a pas été supprimé du serveur."
      );

      setError(null);
    } catch (err) {
      console.error("Erreur lors de la simulation de suppression:", err);
      setError(
        "Impossible de supprimer le quiz. Veuillez réessayer plus tard."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold">Évaluation & Quizzes</h1>
      <p className="mt-4">Gérez les évaluations et quizzes de la plateforme.</p>
      <div className="mt-8 bg-white p-6 rounded-lg shadow">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <h2 className="text-xl font-semibold">Bibliothèque d'évaluations</h2>
          <div className="flex flex-col md:flex-row gap-4 mt-4 md:mt-0 w-full md:w-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher..."
                className="pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <svg
                className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <button
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center"
              onClick={() => setShowModal(true)}
              disabled={createLoading}
            >
              <svg
                className="w-5 h-5 mr-2"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
              Créer Quiz
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : error ? (
          <div className="text-red-500 py-4">{error}</div>
        ) : filteredQuizzes.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    #
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Titre
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Cours associé
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Date de création
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredQuizzes.map((quiz, index) => (
                  <tr key={quiz._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{index + 1}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {quiz.title || "Quiz sans titre"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-700">
                        {getCourseName(quiz)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {formatDate(quiz.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex space-x-3">
                        <button
                          className="text-blue-600 hover:text-blue-900"
                          onClick={() => viewQuizDetails(quiz._id)}
                          title="Voir les détails"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                            <path
                              fillRule="evenodd"
                              d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                        <button
                          className="text-indigo-600 hover:text-indigo-900"
                          onClick={() => handleEditQuiz(quiz._id)}
                          title="Modifier"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          </svg>
                        </button>
                        <button
                          className="text-red-600 hover:text-red-900"
                          onClick={() => handleDeleteQuiz(quiz._id)}
                          title="Supprimer"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>Aucune évaluation pour le moment.</p>
        )}
      </div>

      {/* Modal pour créer un quiz */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-start pt-10">
          <div className="relative bg-white rounded-lg shadow-xl mx-auto max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white z-10 flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-medium">
                {currentStep === 0
                  ? isEditMode
                    ? `Modifier le quiz: ${selectedQuiz?.title || ""}`
                    : "Créer un nouveau quiz"
                  : `Question ${currentStep} sur ${
                      formData.questions.length + 1
                    }`}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="p-6">
              <form
                onSubmit={isEditMode ? updateQuiz : createQuiz}
                className="space-y-6"
              >
                {renderStep()}

                <div className="flex justify-between space-x-3 sticky bottom-0 bg-white p-4 border-t">
                  <button
                    type="button"
                    onClick={handlePrevious}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    {currentStep === 0 ? "Annuler" : "Précédent"}
                  </button>

                  <div className="flex space-x-3">
                    {/* Bouton Soumettre pour création ou mise à jour */}
                    {(currentStep > 0 ||
                      (isEditMode && formData.questions.length > 0)) && (
                      <button
                        type="submit"
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
                        disabled={createLoading}
                      >
                        {createLoading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        ) : null}
                        {isEditMode ? "Mettre à jour" : "Créer le quiz"}
                      </button>
                    )}

                    {/* Bouton Suivant/Ajouter question */}
                    {currentStep === 0 ? (
                      <button
                        type="button"
                        onClick={() => {
                          if (isEditMode && formData.questions.length > 0) {
                            // En mode édition, charger la première question
                            loadExistingQuestion(0);
                          } else {
                            // En mode création, passer à la création de question
                            setCurrentStep(1);
                          }
                        }}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                        disabled={
                          formData.title === "" || formData.description === ""
                        }
                      >
                        {isEditMode && formData.questions.length > 0
                          ? "Voir les questions"
                          : "Suivant"}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleAddQuestion}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                      >
                        {isEditMode && currentStep <= formData.questions.length
                          ? currentStep === formData.questions.length
                            ? "Enregistrer et ajouter une autre question"
                            : "Enregistrer et continuer vers question " +
                              (currentStep + 1)
                          : "Ajouter une autre question"}
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal pour afficher les détails du quiz */}
      {showDetailsModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-start pt-10">
          <div className="relative bg-white rounded-lg shadow-xl mx-auto max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white z-10 flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-medium">
                Détails du quiz: {selectedQuiz?.title || "Quiz"}
              </h3>
              <button
                onClick={closeDetailsModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="p-6">
              {loadingDetails ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : quizDetails ? (
                <div className="space-y-6">
                  {/* Informations générales */}
                  <div className="bg-white p-4 rounded-md shadow-sm">
                    <h4 className="text-md font-medium mb-4">
                      Informations générales
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Titre
                        </p>
                        <p className="mt-1">
                          {quizDetails.title ||
                            selectedQuiz?.title ||
                            "Non spécifié"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Description
                        </p>
                        <p className="mt-1">
                          {quizDetails.description || "Non spécifiée"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Cours associé
                        </p>
                        <p className="mt-1">
                          {getCourseName(quizDetails || selectedQuiz || {})}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Module
                        </p>
                        <p className="mt-1">
                          {typeof quizDetails?.moduleId === "number"
                            ? getModuleName(quizDetails.moduleId)
                            : typeof selectedQuiz?.moduleId === "number"
                            ? getModuleName(selectedQuiz.moduleId)
                            : "Non spécifié"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Date de création
                        </p>
                        <p className="mt-1">
                          {quizDetails.createdAt
                            ? formatDate(quizDetails.createdAt)
                            : selectedQuiz?.createdAt
                            ? formatDate(selectedQuiz.createdAt)
                            : "Non spécifiée"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Limite de temps
                        </p>
                        <p className="mt-1">
                          {quizDetails.timeLimit || "Non spécifiée"} minutes
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Score de passage
                        </p>
                        <p className="mt-1">
                          {quizDetails.passingScore || "Non spécifié"}%
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Statut
                        </p>
                        <p className="mt-1">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              quizDetails.isActive
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {quizDetails.isActive ? "Actif" : "Inactif"}
                          </span>
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Mélanger les questions
                        </p>
                        <p className="mt-1">
                          {quizDetails.shuffleQuestions ? "Oui" : "Non"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Mélanger les réponses
                        </p>
                        <p className="mt-1">
                          {quizDetails.shuffleAnswers ? "Oui" : "Non"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Tentatives max
                        </p>
                        <p className="mt-1">
                          {quizDetails.maxAttempts || "Illimitées"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Questions */}
                  {quizDetails.questions && quizDetails.questions.length > 0 ? (
                    <div className="bg-white p-4 rounded-md shadow-sm">
                      <h4 className="text-md font-medium mb-4">
                        Questions ({quizDetails.questions.length})
                      </h4>
                      <div className="space-y-4">
                        {quizDetails.questions.map(
                          (question: QuizQuestion, index: number) => (
                            <div
                              key={index}
                              className="border border-gray-200 rounded-md p-4"
                            >
                              <h5 className="font-medium mb-2">
                                Question {index + 1}
                              </h5>
                              <p className="mb-2">{question.text}</p>

                              {question.explanation && (
                                <div className="mb-2">
                                  <p className="text-sm font-medium text-gray-500">
                                    Explication
                                  </p>
                                  <p className="text-sm">
                                    {question.explanation}
                                  </p>
                                </div>
                              )}

                              <div className="mb-2">
                                <p className="text-sm font-medium text-gray-500">
                                  Type
                                </p>
                                <p className="text-sm">
                                  {question.type === "qcm"
                                    ? "QCM"
                                    : question.type === "qcu"
                                    ? "QCU"
                                    : "Texte"}
                                </p>
                              </div>

                              <div className="mb-4">
                                <p className="text-sm font-medium text-gray-500">
                                  Points
                                </p>
                                <p className="text-sm">{question.points}</p>
                              </div>

                              {question.answers &&
                                question.answers.length > 0 && (
                                  <div>
                                    <p className="text-sm font-medium text-gray-500 mb-2">
                                      Réponses
                                    </p>
                                    <div className="space-y-2 pl-4">
                                      {question.answers.map(
                                        (
                                          answer: QuizAnswer,
                                          aIndex: number
                                        ) => (
                                          <div
                                            key={aIndex}
                                            className={`p-2 rounded ${
                                              answer.isCorrect
                                                ? "bg-green-50 border border-green-200"
                                                : "bg-gray-50 border border-gray-200"
                                            }`}
                                          >
                                            <p className="flex items-center">
                                              {answer.isCorrect && (
                                                <svg
                                                  className="h-4 w-4 text-green-500 mr-1"
                                                  xmlns="http://www.w3.org/2000/svg"
                                                  viewBox="0 0 20 20"
                                                  fill="currentColor"
                                                >
                                                  <path
                                                    fillRule="evenodd"
                                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                    clipRule="evenodd"
                                                  />
                                                </svg>
                                              )}
                                              {answer.text}
                                            </p>
                                            {answer.feedback && (
                                              <p className="text-xs mt-1 text-gray-500">
                                                Feedback: {answer.feedback}
                                              </p>
                                            )}
                                          </div>
                                        )
                                      )}
                                    </div>
                                  </div>
                                )}
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white p-4 rounded-md shadow-sm">
                      <p className="text-center text-gray-500">
                        Aucune question disponible
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-red-500 py-4">
                  Impossible de charger les détails du quiz
                </div>
              )}

              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={closeDetailsModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEvaluation;
